/**
 * This file contains logic for a homegrown view virtualizer, currently just used for
 * polymer maps (including alignments). It is based on Backbone and operates on elements
 * that are Backbone view classes (called SliceViews internally).
 *
 * Ideally, in the future we'll be able to migrate to a setup where "slices" are React components
 * and we can use an existing library like react-window to handle the virtualization logic.
 *
 * Also note that the views are virtualized vertically (as you'd probably expect) but we also
 * perform horizontal virtualizing separately for alignment rows and the minimap (AKA linear map).
 */
// TODO: This file was created by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS103: Rewrite code to no longer use __guard__
 * DS104: Avoid inline assignments
 * DS205: Consider reworking code to avoid use of IIFEs
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {clamp} from '@benchling/js-utils';
import Backbone from 'backbone';
import * as d3 from 'd3';
import $ from 'jquery';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import * as context from 'Context';
import Slice from 'molbio/maps/linear/Slice';
import {TabState} from 'Navigation';
import {Optional} from 'util/Types';

export interface ItemSourceAttributes<ConcreteSliceView extends MinimalSliceView> {
  length: number;
  scroller?: InfiniteScroller<ConcreteSliceView>;
}

interface ItemSource<ConcreteSliceView extends MinimalSliceView> {
  get<K extends keyof ItemSourceAttributes<ConcreteSliceView>>(
    key: K
  ): ItemSourceAttributes<ConcreteSliceView>[K];
  set<K extends keyof ItemSourceAttributes<ConcreteSliceView>>(
    attributeName: K,
    value: ItemSourceAttributes<ConcreteSliceView>[K],
    options?: Backbone.ModelSetOptions
  ): ItemSource<ConcreteSliceView>;
  set(obj: Partial<ItemSourceAttributes<ConcreteSliceView>>): ItemSource<ConcreteSliceView>;
  // If defined, recycle an existing SliceView class to render the slice
  // at the specified index.
  updateItem?(sliceView: ConcreteSliceView, index: number): void;
}

abstract class ItemSource<ConcreteSliceView extends MinimalSliceView> extends Backbone.Model {
  /**
   * An abstract model class that tracks the state of the entity being rendered.
   * It is responsible for knowing how many "slices" can be rendered and for
   * generating the SliceView instances dynamically when `getItem()` is called.
   *
   * In the example of a sequence or protein entity, this model would know about
   * the zoom, number of residues, etc., be able to determine how many slices are
   * needed to draw those residues, and create the SliceView instances on demand.
   *
   * Since each slice can be a different height (based on how many annotations, etc. are present)
   * this class provides an `estimatedHeight()` method that just provides a best-guess at
   * how big each slice will be. This is used for rendering the scrollbar, but may be misleading
   * because the exact heights are not known ahead of time. Determining the actual height needed is
   * the responsibility of the individual SliceView instances.
   *
   * The implementation has some example values, but we should really make more
   * of them abstract.
   */
  override defaults(): ItemSourceAttributes<ConcreteSliceView> {
    return {length: 0};
  }

  abstract getItem(index: number): ConcreteSliceView;

  length(): number {
    return this.get('length');
  }
  estimatedHeight(): number {
    return 100;
  }

  // Seemingly unused but there is an `afterAttach` method for the SliceView instances that is invoked.
  afterAttach(): void {}
}

interface ScrollDataAttributes {
  // Index of the first slice currently rendered.
  position: number;
  // Index of the last slice currently rendered.
  endPosition: number;
  // Total number of slices.
  size: number;
  enabled: boolean;
}

interface ScrollData {
  get<K extends keyof ScrollDataAttributes>(key: K): ScrollDataAttributes[K];
  set<K extends keyof ScrollDataAttributes>(
    attributeName: K,
    value: ScrollDataAttributes[K],
    options?: Backbone.ModelSetOptions & {who?: string}
  ): ScrollData;
  set(obj: Partial<ScrollDataAttributes>): ScrollData;
}

class ScrollData extends Backbone.Model {
  override defaults(): ScrollDataAttributes {
    return {
      position: 0,
      endPosition: 0,
      size: 10,
      enabled: false,
    };
  }

  setSize(size: number): void {
    this.set('size', size);
  }

  setPosition(pos: number, options?: Backbone.ModelSetOptions & {who?: string}): void {
    this.set('position', Math.max(0, pos), options);
  }

  disable(): void {
    this.set('enabled', false);
  }
}

interface InfiniteScrollbarOptions extends Backbone.ViewOptions<ScrollData> {
  fullHeight?: () => number;
  tabstate: TabState;
}

class InfiniteScrollbar extends Backbone.View<ScrollData> {
  /**
   * View class to render a custom scrollbar for a virtualized list.
   *
   * Provides an estimate of the full size based on the fraction of
   * slices currently in view.
   */
  fullHeight!: () => number;
  tabstate!: TabState;
  $thumb!: JQuery;

  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'infinite-scrollbar';
  }

  constructor(options: InfiniteScrollbarOptions) {
    super(options);
  }

  override initialize(options: InfiniteScrollbarOptions): void {
    _.defaults(options, {
      fullHeight() {
        return this.tabstate.get('bufferViewLayout').get('height');
      },
    });
    this.tabstate = options.tabstate;
    this.fullHeight = options.fullHeight!;
  }

  registerListeners(): void {
    this.listenTo(this.model, 'change:position change:endPosition change:size', () => {
      this.updateThumb();
    });
  }

  updateThumb(): void {
    const pos = this.model.get('position');
    const size = this.model.get('size');
    const visible = this.model.get('endPosition') - pos + 1;

    if (visible >= size) {
      // No need to show scrollbar when all items are visible.
      this.$thumb.hide();
    } else {
      this.$thumb.show();
    }

    const fullHeight = this.fullHeight();
    const thumbHeight = (fullHeight / size) * visible;
    const extraHeight = Math.max(5 - thumbHeight, 0);
    const showHeight = fullHeight - extraHeight;
    const maxTop = fullHeight - thumbHeight;
    const y = Math.min(maxTop, (pos / size) * showHeight);
    const top = `${y}px`;
    const height = `${thumbHeight}px`;
    d3.select(this.el).datum({x: 0, y});
    // To do, this causes a reflow :
    this.$thumb.css('top', top);
    this.$thumb.css('height', height);
    this.$thumb.css('min-height', '5px');
  }

  handleDrag(e: MouseEvent, d: {x: number; y: number}): void {
    this.setPosition(d, e.y);
  }

  setPosition(d: {x: number; y: number}, y: number): void {
    const height = this.$el.height();
    const size = this.model.get('size');
    y = clamp(0, height, y);
    d.y = y;
    const pos = clamp(0, size - 1, (y / height) * size);
    this.model.setPosition(pos, {who: this.cid});
  }

  posFromY(y: number): number {
    const height = this.$el.height();
    const size = this.model.get('size');
    y = clamp(0, height, y);
    return clamp(0, size - 1, (y / height) * size);
  }

  handleDragEnd(d: {x: number; y: number}): void {
    this.$el.removeClass('drag');
  }

  handleDragStart(d: {x: number; y: number}): void {
    this.$el.addClass('drag');
  }

  override render(): this {
    this.registerListeners();
    this.$thumb = $('<div class="infinite-scrollthumb"></div>');

    const origin = (e: Event, d: {x: number; y: number}): {x: number; y: number} => {
      const [_, y] = d3.pointer(e, this.el);
      const position = this.posFromY(y);
      if (!(this.model.get('position') <= position && position <= this.model.get('endPosition') + 1)) {
        this.setPosition(d, y);
      }
      return d;
    };

    const drag = d3
      .drag()
      .subject(origin)
      .on('drag', (e, d: {x: number; y: number}) => {
        this.handleDrag(e, d);
      })
      .on('start', (e, d: {x: number; y: number}) => {
        this.handleDragStart(d);
      })
      .on('end', (e, d: {x: number; y: number}) => {
        this.handleDragEnd(d);
      });

    d3.select(this.el).datum({x: 0, y: 0}).call(drag);
    this.updateThumb();
    this.$el.append(this.$thumb);
    return this;
  }
}
InfiniteScrollbar.initClass();

interface InfiniteScrollerOptions<
  ConcreteSliceView extends MinimalSliceView,
> extends Backbone.ViewOptions<Backbone.Model> {
  tabstate: TabState;
  mapstate?: unknown;
  itemSource: ItemSource<ConcreteSliceView>;
  scrollData: ScrollData;
  spacing?: number;
}

class InfiniteScroller<ConcreteSliceView extends MinimalSliceView> extends Backbone.View<Backbone.Model> {
  height!: number;
  tabstate!: TabState;
  mapstate!: unknown;
  itemSource!: ItemSource<ConcreteSliceView>;
  scrollData!: ScrollData;
  spacing!: number;
  topIndex!: null | number;
  repositionRequestedOnAnimFrame!: boolean;
  renderedItems!: Array<InfiniteItem<ConcreteSliceView>>;
  indexMap!: {[index: number]: InfiniteItem<ConcreteSliceView>};
  itemsToRemove!: Array<InfiniteItem<ConcreteSliceView>>;
  reserveIndexMap!: {[index: number]: InfiniteItem<ConcreteSliceView>};
  lastMouseY!: null | number;
  lastMouseX!: null | number;
  $container!: JQuery;

  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'infinite-scroller draggable-svg-container';
  }

  constructor(options: InfiniteScrollerOptions<ConcreteSliceView>) {
    super(options);
  }

  override events(): Backbone.EventsHash {
    return {
      scroll: 'handleScroll',
      mousemove: 'saveMouseCoordinates',
    };
  }

  override initialize(options: InfiniteScrollerOptions<ConcreteSliceView>): void {
    this.height = 30000;
    this.tabstate = options.tabstate;
    this.mapstate = options.mapstate;
    this.itemSource = options.itemSource;
    this.scrollData = options.scrollData;
    this.spacing = options.spacing || 0;
    this.topIndex = null;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.scrollData, 'change:position', this.positionHandler);
    this.repositionRequestedOnAnimFrame = false;

    this.renderedItems = [];
    this.indexMap = {};

    this.itemsToRemove = [];
    this.reserveIndexMap = {};

    this.lastMouseY = null;
    this.lastMouseX = null;
  }

  canReuseItems(): boolean {
    return this.itemSource.updateItem != null;
  }

  items(): Array<ConcreteSliceView> {
    return this.renderedItems.map((item) => item.item());
  }

  reserveItem(index: number): void {
    const item = this.renderedItems.splice(index, 1)[0];
    if (this.canReuseItems()) {
      item.$el.css('display', 'none');
    }
    delete this.indexMap[item.index()];
    this.reserveIndexMap[item.index()] = item;
    this.itemsToRemove.push(item);
  }

  scrollTop(top: number): void {
    this.$el.scrollTop(top);
  }

  grabItem(sliceIndex: number | null = null): InfiniteItem<ConcreteSliceView> {
    // Only call if itemsToRemove is not empty.
    // If sliceIndex is null, it will return arbitrary item.
    // Otherwise, it will return the specified element or null.

    let index;
    let item = null;
    if (sliceIndex != null) {
      item = this.reserveIndexMap[sliceIndex] || null;
      // eslint-disable-next-line no-restricted-syntax
      index = _.indexOf(this.itemsToRemove, item);
    } else {
      item = this.itemsToRemove[0];
      index = 0;
      sliceIndex = item.index();
    }

    // Remove the desired item.
    if (item != null) {
      this.itemsToRemove.splice(index, 1);
      delete this.reserveIndexMap[sliceIndex];
    }

    // Return the desired item.
    return item;
  }

  makeItem(
    options: Partial<InfiniteItemOptions<ConcreteSliceView>> & {index: number}
  ): InfiniteItem<ConcreteSliceView> {
    const {index} = options;
    let item: null | InfiniteItem<ConcreteSliceView> = null;
    // Try to reuse an item.
    if (this.itemsToRemove.length && this.canReuseItems()) {
      item = this.grabItem(index);
      let needsRerender = false;
      if (item == null) {
        item = this.grabItem();
        needsRerender = true;
      }

      const noRerender = context.get('noRerender', false);
      if (needsRerender || !noRerender) {
        this.itemSource.updateItem!(item.item(), index);
      }

      item.update(options);
      item.$el.css('display', '');
    } else {
      // Otherwise, make a new item.
      // eslint-disable-next-line no-restricted-syntax
      const itemOptions = _.extend(
        {
          item: this.itemSource.getItem(index),
          spacing: this.spacing,
        },
        options
      );
      item = new InfiniteItem<ConcreteSliceView>(itemOptions);
      this.listenTo(item, 'change:height', () => {
        this.repositionOnAnimFrame();
      });
    }
    return item;
  }

  flushReservedItems(): void {
    // hide them if we don't need them.
    // FIXME: should we be dieing them too?
    // They might have listeners (which will waste time).
    // This helps:
    //   1. kill the cost of removing/inserting DOM nodes.
    //   2. prevent https://code.google.com/p/chromium/issues/detail?id=340956
    //        ... but it still seems to be happening :(

    // Remove items
    while (this.itemsToRemove.length) {
      this.grabItem().remove();
    }
    // FIXME: Removing instead of hiding might hurt scrolling performance.
  }

  saveMouseCoordinates(e: JQueryEventObject): void {
    this.lastMouseX = e.pageX;
    this.lastMouseY = e.pageY;
  }

  reposition(addItems: boolean = true): void {
    let prevItem = null;
    for (const item of this.renderedItems) {
      if (prevItem != null) {
        item.top(prevItem.bottom());
      }
      prevItem = item;
    }

    if (addItems) {
      this.addItems();
    }
    this.repositionRequestedOnAnimFrame = false;
  }

  repositionOnAnimFrame(): void {
    if (this.repositionRequestedOnAnimFrame) {
      return;
    }
    this.repositionRequestedOnAnimFrame = true;
    requestAnimationFrame(() => {
      this.reposition();
    });
  }

  positionHandler(
    model: ScrollData,
    value: number,
    options: Backbone.ModelSetOptions & {who?: string}
  ): void {
    if (options.who !== this.cid) {
      let index = this.scrollData.get('position');
      const fraction = index % 1;
      index = Math.floor(index);
      this.jumpToIndex(index, fraction);
    }
  }

  firstRendered(): InfiniteItem<ConcreteSliceView> | null {
    if (this.renderedItems.length > 0) {
      return this.renderedItems[0];
    } else {
      return null;
    }
  }

  lastRendered(): InfiniteItem<ConcreteSliceView> | null {
    const {length} = this.renderedItems;
    if (length > 0) {
      return this.renderedItems[this.renderedItems.length - 1];
    } else {
      return null;
    }
  }

  scrollToIncludeIndex(sliceIndex: number): void {
    if (sliceIndex < this.firstRendered()!.index()) {
      this.jumpToIndex(sliceIndex, 0);
    } else if (sliceIndex === this.firstRendered()!.index()) {
      this.$el.scrollTop(this.firstRendered()!.top());
    } else if (sliceIndex === this.lastRendered()!.index()) {
      this.scrollTop(this.lastRendered()!.bottom() - this.$el.height());
    } else if (sliceIndex >= this.lastRendered()!.index()) {
      this.$container.height((this.height = 15000));
      this.bootstrapBottom({index: sliceIndex, bottomAnchor: 10000});
    }
  }

  setPosition(): ScrollData {
    const scrollTop = this.$el.scrollTop();
    const topoffset = (scrollTop - this.firstRendered()!.top()) / this.firstRendered()!.height();
    const position = Math.max(0, this.firstRendered()!.index() + topoffset);

    const adjustedScrollBottom = scrollTop + this.$el.height();
    const btmoffset = (adjustedScrollBottom - this.lastRendered()!.bottom()) / this.lastRendered()!.height();
    const endPosition = Math.min(this.itemSource.length(), this.lastRendered()!.index() + btmoffset);

    this.scrollData.setPosition(position, {who: this.cid});
    return this.scrollData.set('endPosition', endPosition, {who: this.cid});
  }

  jumpToIndex(index: number, fraction: number = 0): void {
    // Determine what we can recycle.
    // eslint-disable-next-line benchlint/no-in-operator
    if (index in this.indexMap) {
      this.scrollTop(this.indexMap[index].top() + this.indexMap[index].height() * fraction);
      this.setPosition();
    } else {
      this.$container.height((this.height = 15000));
      context.using({noRerender: true}, this, function () {
        // @ts-expect-error: confusing scope
        this.bootstrap({index, topAnchor: 10000});
        // @ts-expect-error: confusing scope
        this.scrollTop(this.firstRendered().top() + this.firstRendered().height() * fraction);
        // @ts-expect-error: confusing scope
        this.handleScroll();
      });
    }
  }

  // Scroll to a reasonable place, and render.
  // The first visible item should have the specified index

  setTopIndex(index: number): number {
    this.topIndex = index;
    // This return value is actually used.
    return index;
  }

  pushDown(offset: number): void {
    this.firstRendered()!.setTopAnchor(this.firstRendered()!.top() + offset);
    this.reposition(false);
    this.scrollTop(this.$el.scrollTop() + offset);
  }

  pullTo(pos: number): void {
    const dy = pos - this.firstRendered()!.top();
    this.firstRendered()!.setTopAnchor(pos);
    this.reposition(false);
    this.scrollTop(this.$el.scrollTop() + dy); // scroll to restore old position...
  }

  addItems(): number {
    let index;
    let newItem;
    let scrollTop = this.$el.scrollTop();
    let scrollBottom = scrollTop + this.$el.height();

    let renderedTop = __guard__(this.firstRendered(), (x) => x.top());
    let renderedBottom = __guard__(this.lastRendered(), (x1) => x1.bottom());

    if (renderedTop == null) {
      throw new Error('InfiniteScroller: No item to anchor to.');
    }

    // For now, just keep expanding in both directions until everything that needs to be rendered is rendered.
    // We aren't removing any slices.

    // Estimate top.

    const oldTopIndex = this.topIndex;

    const sliceChange = Math.floor((renderedTop - scrollTop) / this.itemSource.estimatedHeight());
    const estimatedTop = this.clampIndex(this.topIndex! - sliceChange);

    // Remove from top
    while (this.firstRendered() != null && this.firstRendered()!.bottom() < scrollTop) {
      this.reserveItem(0);
      this.setTopIndex(__guard__(this.firstRendered(), (x2) => x2.index())!);
    }

    while (this.lastRendered() != null && this.lastRendered()!.top() > scrollBottom) {
      this.reserveItem(this.renderedItems.length - 1);
    }

    if (this.firstRendered() == null) {
      this.bootstrap({index: estimatedTop, topAnchor: scrollTop});
      scrollTop = this.$el.scrollTop();
    }

    renderedTop = __guard__(this.firstRendered(), (x3) => x3.top())!;
    // FILL UP
    let bottomAnchor = this.firstRendered()!;
    while (renderedTop > scrollTop && this.topIndex! > 0) {
      // @ts-expect-error
      index = this.setTopIndex(this.topIndex - 1);
      newItem = this.makeItem({index, bottomAnchor: bottomAnchor.top()});

      // If it's not attached, attach it.
      if (!newItem.isAttached()) {
        this.$container.append(newItem.render().el);
      }
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      __guardMethod__(newItem.item(), 'afterAttach', (o) => o.afterAttach());

      this.renderedItems.unshift(newItem);
      this.indexMap[index] = newItem;

      // A freshly-rendered row reports height 0 until it settles asynchronously; this means the virtualizer
      // erroneously includes all rows in the viewport causing performance issues for large alignments.
      // While the real height is unknown, fallback to the nonzero estimated height to prevent this.
      const newItemHeight = newItem.height();
      renderedTop -= newItemHeight > 0 ? newItemHeight : this.itemSource.estimatedHeight();
      bottomAnchor = newItem;
    }

    if (this.topIndex! > 0 && scrollTop < 5000) {
      this.pushDown(5000);
      scrollTop += 5000;
      scrollBottom += 5000;
    }

    if (this.topIndex === 0 && oldTopIndex !== 0) {
      // if we just scrolled to here.
      this.pullTo(0);
      scrollTop = this.$el.scrollTop();
      scrollBottom = scrollTop + this.$el.height();
    }

    renderedBottom = __guard__(this.lastRendered(), (x4) => x4.bottom())!;

    let topAnchor = this.lastRendered()!;
    while (renderedBottom < scrollBottom) {
      index = this.topIndex! + this.renderedItems.length;
      if (index < this.itemSource.length()) {
        newItem = this.makeItem({index, topAnchor: topAnchor.bottom()});
        if (!newItem.isAttached()) {
          this.$container.append(newItem.render().el);
        }
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
        __guardMethod__(newItem.item(), 'afterAttach', (o1) => o1.afterAttach());
        this.renderedItems.push(newItem);
        this.indexMap[index] = newItem;

        // Similar to the fill-up logic above, while the real height is temporarily unknown, fallback to the
        // nonzero estimated height.
        const newItemHeight = newItem.height();
        renderedBottom += newItemHeight > 0 ? newItemHeight : this.itemSource.estimatedHeight();
        topAnchor = newItem;
        if (scrollBottom + 2000 > this.height) {
          this.height += 10000;
          this.$container.height(this.height);
        }
      } else {
        if (this.lastRendered()!.bottom() < this.height) {
          this.height = this.lastRendered()!.bottom();
          this.$container.height(this.height);
        }
        break;
      }
    }
    if (!this.canReuseItems()) {
      this.flushReservedItems();
    }
    const topoff = (scrollTop - this.firstRendered()!.top()) / this.firstRendered()!.height();
    const cutoff = (scrollBottom - this.lastRendered()!.bottom()) / this.lastRendered()!.height();
    this.scrollData.setPosition(Math.max(0, this.firstRendered()!.index() + topoff), {who: this.cid});
    this.scrollData.set(
      'endPosition',
      Math.min(this.itemSource.length(), this.lastRendered()!.index() + cutoff)
    );
    // Make sure that this value is correctly mainatined through the above function call.
    return scrollTop;
  }

  handleResize(targetSlice: number): void {
    // Maintain the same first row of bases.

    const size = this.itemSource.length();
    const position = targetSlice;
    this.scrollData.set({size, position});

    this.bootstrap({index: targetSlice, topAnchor: this.$el.scrollTop()});
    this.addItems();
  }

  handleScroll(): void {
    const adjustScrollTop = this.addItems();

    // Update scrollbar
    const first = this.firstRendered()!;
    const currentIndex = first.index();
    const fraction = (adjustScrollTop - first.top()) / first.height();
    this.scrollData.setPosition(Math.max(0, currentIndex + fraction), {who: this.cid});

    this.trigger('scroll', this.lastMouseX, this.lastMouseY);
  }

  findSliceByTop(yPos: number): ConcreteSliceView {
    const y = this.$el.scrollTop() + yPos;

    if (y < this.firstRendered()!.bottom()) {
      return this.firstRendered()!.item();
    }

    let prevItem = this.firstRendered()!;
    for (const item of this.renderedItems) {
      const top = item.top();
      const bottom = item.bottom();
      if (top < y && y <= bottom) {
        return prevItem.item();
      }
      prevItem = item;
    }

    return this.lastRendered()!.item();
  }

  bootstrapBottom(
    options: Partial<InfiniteItemOptions<ConcreteSliceView>> & {flush?: boolean} & {
      index: number;
      bottomAnchor: number;
    }
  ): void {
    this.bootstrap(options);
    this.scrollTop(options.bottomAnchor - this.$el.height());
  }

  clampIndex(index: number): number {
    return clamp(0, this.itemSource.length() - 1, index);
  }

  bootstrap(
    options: Partial<InfiniteItemOptions<ConcreteSliceView>> & {flush?: boolean} & {index: number}
  ): void {
    options.index = this.clampIndex(options.index);
    const {index} = options;
    // Kill all things old.
    while (this.renderedItems.length) {
      this.reserveItem(this.renderedItems.length - 1);
    }

    if (options.flush) {
      this.flushReservedItems();
    }

    this.indexMap = {};

    const newItem = this.makeItem(options);
    this.setTopIndex(index);
    if (!newItem.isAttached()) {
      this.$container.append(newItem.render().el);
    }
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    __guardMethod__(newItem.item(), 'afterAttach', (o) => o.afterAttach());

    this.renderedItems = [newItem];
    this.indexMap[index] = newItem;

    if (index === 0) {
      this.pullTo(0);
    }
  }

  override remove(): Backbone.View<Backbone.Model> {
    for (const item of this.renderedItems) {
      item.remove();
    }

    // We need to explicitly remove all items we've kept around, because we need to
    // call .die() to properly stopListening for all Components.
    this.flushReservedItems();

    this.scrollData.disable();
    $(window).off(`.${this.cid}`);
    return super.remove();
  }

  handleSourceLength(): void {
    const size = this.itemSource.length();
    this.scrollData.setSize(size);
    while (this.lastRendered() != null && this.lastRendered()!.index() >= size) {
      this.reserveItem(this.renderedItems.length - 1);
    }

    if (this.lastRendered() == null) {
      this.bootstrap({index: 0});
    }
    this.addItems();
  }

  registerListeners(): void {
    if (benchlingUtil.firefox) {
      $(window).on(`beforeunload.${this.cid}`, () => {
        window.beforeUnloadType = 'infinitescroller';
        this.$el.scrollTop(0);
      });
    }
    this.listenTo(this.itemSource, 'resizeY', () => {
      this.handleScroll();
    });
    this.listenTo(this.itemSource, 'resizeX', (targetSliceIndex: number) => {
      this.handleResize(targetSliceIndex);
    });
    this.listenTo(this.itemSource, 'change:length', () => {
      this.handleSourceLength();
    });
  }

  override render(): this {
    this.registerListeners();
    this.$container = $('<div class="infinite-container"></div>');
    this.$el.append(this.$container);
    this.$container.css('height', this.height);

    // attach first item.
    // NOTE: the fallback of 0 should never be used but keeping for safety currently.
    const index = this.clampIndex(Math.floor(this.scrollData.get('position'))) || 0;
    this.bootstrap({index, topAnchor: 0});

    this.scrollData.setSize(this.itemSource.length());
    this.scrollData.setPosition(index, {who: this.cid});

    _.defer(() => this.trigger('scroll')); // hack: waiting for all items to render before fetching initial cutsites
    return this;
  }
}
InfiniteScroller.initClass();

// Interface that SliceView classes are expected to implement.
export interface MinimalSliceView extends Backbone.View<Backbone.Model> {
  getSlice(): Slice;
  height(): number;
  die(): void;
  afterAttach?(): void;
}

interface InfiniteItemOptions<
  ConcreteSliceView extends MinimalSliceView,
> extends Backbone.ViewOptions<Backbone.Model> {
  index: number;
  item: ConcreteSliceView;
  topAnchor: number;
  bottomAnchor: number;
  spacing?: number;
}

class InfiniteItem<ConcreteSliceView extends MinimalSliceView> extends Backbone.View<Backbone.Model> {
  /**
   * A wrapper view around a SliceView that is being rendered in a virtualized list.
   * Tracks the height of the slice, any additional, spacing between list rows, and
   * an absolute position of where to draw the element.
   */
  _index!: number;
  _item!: ConcreteSliceView;
  _topAnchor!: number;
  _bottomAnchor!: number;
  _spacing!: number;
  _top!: number;

  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'infinite-item';
  }

  override initialize(options: InfiniteItemOptions<ConcreteSliceView>): void {
    this._index = options.index;
    this._item = options.item;
    this._topAnchor = options.topAnchor;
    this._bottomAnchor = options.bottomAnchor;
    this._spacing = options.spacing || 0;
    this._top = 0;
    this.listenTo(this.getSlice(), 'change:height', function () {
      // @ts-expect-error confusing shadowing. Is `this` bound to InfiniteItem instance or the slice instance?
      return this.trigger('change:height');
    });
    this._checkValid();
  }

  _checkValid(): void {
    if (this._index < 0) {
      throw new Error(`Invalid slice index specified ${this._index}`);
    }
  }

  top(value: number): void;
  top(value?: never): number;
  // Need to use any because of function overloads.
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  top(value: any): any {
    if (value != null) {
      this._top = value;
      this.$el.css('top', `${this._top}px`);
    } else {
      return this._top;
    }
  }

  bottom(): number {
    return this.top() + this.height();
  }
  height(): number {
    return this._item.height() + this._spacing;
  }
  item(): ConcreteSliceView {
    return this._item;
  }
  index(): number {
    return this._index;
  }

  isAttached(): boolean {
    return this.$el.parent().length > 0;
  }

  override remove(): Backbone.View<Backbone.Model> {
    this._item.die();
    this._item.remove();
    return super.remove();
  }

  getSlice(): Slice {
    return this._item.getSlice();
  }

  update(
    options: Partial<Pick<InfiniteItemOptions<ConcreteSliceView>, 'index' | 'topAnchor' | 'bottomAnchor'>>
  ): void {
    // eslint-disable-next-line benchlint/no-in-operator
    if ('index' in options) {
      this.setIndex(options.index!);
    }
    // eslint-disable-next-line benchlint/no-in-operator
    if ('topAnchor' in options) {
      this.setTopAnchor(options.topAnchor!);
    }
    // eslint-disable-next-line benchlint/no-in-operator
    if ('bottomAnchor' in options) {
      this.setBottomAnchor(options.bottomAnchor!);
    }
  }

  setIndex(idx: number): void {
    this._index = idx;
    this._checkValid();
  }

  setTopAnchor(top: number): void {
    this.top(top);
  }

  setBottomAnchor(anchor: number): void {
    this.top(anchor - this.height());
  }

  updatePos(): void {
    if (this._topAnchor != null) {
      this.setTopAnchor(this._topAnchor);
    } else if (this._bottomAnchor != null) {
      this.setBottomAnchor(this._bottomAnchor);
    } else {
      throw new Error('InfiniteScroller: item has no neighbor anchor set.');
    }
  }

  override render(): this {
    this.$el.append(this._item.render().el);
    this.updatePos();
    return this;
  }
}
InfiniteItem.initClass();

export {ScrollData, ItemSource, InfiniteScroller, InfiniteScrollbar, InfiniteItem};

function __guard__<V, T>(value: Optional<V>, transform: (value: V) => T): T | undefined {
  return value != null ? transform(value) : undefined;
}
function __guardMethod__<V extends object, T>(
  obj: Optional<V>,
  methodName: string,
  transform: (obj: V, methodName: string) => T
): T | undefined {
  // @ts-expect-error
  if (obj != null && typeof obj[methodName] === 'function') {
    return transform(obj, methodName);
  } else {
    return undefined;
  }
}
